import { motion } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { useClubs } from '@/features/clubs/hooks/useClubs';
import { useLoadingContext } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { getBestImageUrl, getBestVideoUrl } from '@/lib/utils';
import { ImagePlaceholder } from '@/features/home/utils';
import { likeService } from '@/features/likes/services/likeService';
import LikeButton from '@/components/LikeButton';
import { useState, useEffect } from 'react';

export default function ClubsSection() {
  const { clubs, loading, error } = useClubs();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isAuthenticated } = useAuth();
  const [hoveredClubId, setHoveredClubId] = useState<string | null>(null);
  const [shouldPlayVideo, setShouldPlayVideo] = useState<string | null>(null);
  const [likedMap, setLikedMap] = useState<Record<string, boolean>>({});

  // Trigger loading when data is loading
  useEffect(() => {
    if (loading) {
      startLoading('clubs-data');
    } else {
      stopLoading('clubs-data');
    }
  }, [loading, startLoading, stopLoading]);

  useEffect(() => {
    if (hoveredClubId) {
      const timer = setTimeout(() => {
        setShouldPlayVideo(hoveredClubId);
      }, 300); // 300ms delay
      return () => clearTimeout(timer);
    } else {
      setShouldPlayVideo(null);
    }
  }, [hoveredClubId]);

  // Fetch like status for clubs on initial load when user is authenticated
  // Note: Intentionally not re-fetching when isAuthenticated changes mid-session
  // to avoid race conditions with toggle operations
  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!isAuthenticated || clubs.length === 0) return;
      
      const clubIds = clubs.slice(0, 4).map(club => club.id);
      const response = await likeService.checkLikes({
        entity_ids: clubIds,
        entity_type: 'businesses',
      });
      setLikedMap(response.liked_map);
    };

    fetchLikeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubs]);

  const handleLikeToggle = (clubId: string, newLikedState: boolean) => {
    setLikedMap(prev => ({ ...prev, [clubId]: newLikedState }));
  };

  const handleClubClick = (clubHandle: string) => {
    navigate(`/venues/${clubHandle}`);
  };

  const handleViewAllClubs = () => {
    navigate('/venues');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
    hover: {
      y: -8,
      transition: {
        duration: 0.3,
      },
    },
  };

  if (loading) {
    return (
      <section className="max-w-8xl mx-auto px-8 py-24">
        <div className="text-center">
          {/* Loading is handled by the global loader */}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-8xl mx-auto px-8 py-24">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-gray-400">Unable to load venues at the moment</p>
        </div>
      </section>
    );
  }

  // Show only first 4 clubs on homepage for better UX (matching the grid pattern)
  const displayedClubs = clubs.slice(0, 4);

  return (
    <section id="clubs" className="max-w-8xl mx-auto px-8 py-24">
      <motion.div 
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-5xl font-serif font-medium text-white">Featured Venues</h2>
        <p className="text-lg text-gray-400 mt-4 max-w-2xl mx-auto">
          Experience the most exclusive and vibrant nightlife destinations around the world.
        </p>
      </motion.div>

      {displayedClubs.length > 0 ? (
        <>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 mb-16"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {displayedClubs.map((club) => {
              const videoUrl = getBestVideoUrl(club.card_video_id);
              const shouldPlay = shouldPlayVideo === club.id;
              
              return (
                <motion.div 
                  key={club.id} 
                  className="group relative overflow-hidden rounded-3xl cursor-pointer"
                  variants={cardVariants}
                  whileHover="hover"
                  onClick={() => handleClubClick(club.handle)}
                  onMouseEnter={() => setHoveredClubId(club.id)}
                  onMouseLeave={() => setHoveredClubId(null)}
                >
                  <div className="aspect-[3/4]">
                    {shouldPlay && videoUrl ? (
                      <video
                        src={videoUrl}
                        autoPlay
                        muted
                        loop
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to image if video fails to load
                          const target = e.target as HTMLVideoElement;
                          target.style.display = 'none';
                          const img = target.nextElementSibling as HTMLImageElement;
                          if (img) img.style.display = 'block';
                        }}
                      />
                    ) : null}
                    {getBestImageUrl(club.card_image_id, club.media, '') !== '' ? (
                      <img
                        src={getBestImageUrl(club.card_image_id, club.media, '/placeholder-club.jpg')}
                        alt={club.name}
                        className={`w-full h-full object-cover ${shouldPlay && videoUrl ? 'hidden' : 'block'}`}
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <ImagePlaceholder 
                      label={`${club.name} Venue`} 
                      className={getBestImageUrl(club.card_image_id, club.media, '') !== '' ? 'hidden' : ''} 
                    />
                </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent transition-all duration-500 group-hover:from-black/90"></div>
                  
                  {/* Like Button - Top Right */}
                  <LikeButton
                    entityId={club.id}
                    entityType="businesses"
                    isLiked={likedMap[club.id] || false}
                    onToggle={(newState) => handleLikeToggle(club.id, newState)}
                    className="absolute top-4 right-4 z-10"
                  />
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-1/3 group-hover:translate-y-0 transition-transform duration-500 ease-out">
                    <h3 className="text-2xl font-serif font-medium mb-2">{club.name}</h3>
                  <p className="text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100 mb-2">
                    {club.location.city}, {club.location.country}
                  </p>
                </div>
              </motion.div>
              );
            })}
          </motion.div>
          
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <button
              onClick={handleViewAllClubs}
              className="inline-flex items-center px-8 py-4 bg-white/95 backdrop-blur-sm text-black font-medium rounded-full hover:bg-white transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              View All Venues
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </motion.div>
        </>
      ) : (
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-400 mb-4">No venues available at the moment</p>
          <button
            onClick={handleViewAllClubs}
            className="inline-flex items-center px-6 py-3 bg-white/95 backdrop-blur-sm text-black font-medium rounded-full hover:bg-white transition-colors duration-200"
          >
            Check Back Later
          </button>
        </motion.div>
      )}
    </section>
  );
}
